import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import type { Row } from "./api";
import { useAdmin } from "./context";
import { day, staffChange } from "./format";
import { Empty, LoadError, PageHead } from "./parts";

type Filters = { since: string; until: string; staff: string; case_type: string };

/** P3: every staff change across customers, filterable, with a count per case type and a CSV export. */
export function StaffChangesTab() {
  const { key, api, caseTypes, caseType, openCustomer } = useAdmin();
  const [f, setF] = useState<Filters>({ since: "", until: "", staff: "", case_type: "" });
  const [staffText, setStaffText] = useState("");
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  const q = useQuery({
    queryKey: [key, "overrides", qs],
    queryFn: () => api.get<Row>(`overrides${qs ? `?${qs}` : ""}`),
  });
  const counts = Object.entries((q.data?.counts || {}) as Record<string, number>).sort((a, b) => b[1] - a[1]);
  const rows: Row[] = q.data?.rows || [];
  return (
    <div>
      <PageHead title="Staff changes" sub="Every programme change made by staff, newest first. Use it to improve the algorithm." />
      <Card>
        <CardContent className="space-y-4">
          <div className="grid items-end gap-3 md:grid-cols-[repeat(4,1fr)_auto]">
            <div><Label htmlFor="sc-since" className="text-ks-grey-1">From</Label>
              <Input id="sc-since" type="date" data-nonmutating value={f.since} onChange={(e) => setF({ ...f, since: e.target.value })} /></div>
            <div><Label htmlFor="sc-until" className="text-ks-grey-1">To</Label>
              <Input id="sc-until" type="date" data-nonmutating value={f.until} onChange={(e) => setF({ ...f, until: e.target.value })} /></div>
            <div><Label htmlFor="sc-staff" className="text-ks-grey-1">Changed by</Label>
              <Input id="sc-staff" data-nonmutating placeholder="Everyone (email)" value={staffText}
                onChange={(e) => setStaffText(e.target.value)}
                onBlur={() => setF({ ...f, staff: staffText.trim() })}
                onKeyDown={(e) => { if (e.key === "Enter") setF({ ...f, staff: staffText.trim() }); }} /></div>
            <div><Label htmlFor="sc-ct" className="text-ks-grey-1">Case type</Label>
              <select id="sc-ct" data-nonmutating value={f.case_type} onChange={(e) => setF({ ...f, case_type: e.target.value })}
                className="h-9 w-full rounded-lg border border-input bg-transparent px-2 text-sm">
                <option value="">All case types</option>
                {caseTypes.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select></div>
            <Button variant="outline" asChild data-nonmutating>
              <a href={api.url(`overrides.csv${qs ? `?${qs}` : ""}`)} data-nonmutating>Export CSV</a>
            </Button>
          </div>
          {q.isLoading ? (
            <Empty>Loading…</Empty>
          ) : q.error ? (
            <LoadError what="load" error={q.error} />
          ) : (
            <>
              {counts.length ? (
                <div className="flex flex-wrap gap-2">
                  {counts.map(([k, n]) => <Badge key={k} variant="outline">{caseType(k)}: <b>{n}</b></Badge>)}
                </div>
              ) : null}
              {rows.length ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead><TableHead>Customer</TableHead><TableHead>Change</TableHead>
                        <TableHead>By</TableHead><TableHead>Reason</TableHead><TableHead>Case</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((r) => (
                        <TableRow key={r.id} className="cursor-pointer" onClick={() => openCustomer(r.user_id)}>
                          <TableCell className="whitespace-nowrap">{day(r.at)}</TableCell>
                          <TableCell className="font-semibold">{r.name || r.email}</TableCell>
                          <TableCell className="whitespace-normal">{staffChange(r)}</TableCell>
                          <TableCell>{r.staff_email}</TableCell>
                          <TableCell className="whitespace-normal">{r.reason}</TableCell>
                          <TableCell className="whitespace-nowrap">{caseType(r.case_type)} · {r.case_id}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {rows.length >= 1000 ? (
                    <p className="text-sm text-ks-grey-1">Showing the newest 1,000. Narrow the dates to see older ones; the counts cover every match.</p>
                  ) : null}
                </>
              ) : (
                <Empty>
                  {qs ? "No staff changes match these filters."
                    : "No staff changes yet. Each change staff make to a programme is listed here with who, when and why."}
                </Empty>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
